import APIClient, { failSafe, makeCancellableAPIRequest } from "./index";
import moment from "moment";
import _, { isEmpty, isString, noop, random } from 'lodash'
import { convertDateFilterValueToDate, getFindingAPIDurationFilter, getStartAndEndDateForQuickOption } from "utils/dateUtils";
import { excludePrefix } from "components/ColumnFilterSearchBoxWithExclude";
import { DATE_FILTER_API_SUPPORT, FINDING_STATUS } from "views/Findings/Constants";
import GlobalSettingAPI from "./GlobalSettingAPI";
import { convertFindingStatsToCount } from "utils/columnFilterUtil";

const FindingAPI = {
    getAll(queryParams = {}, showDefaultMessage=true){
        queryParams = modifyFilters(queryParams);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/",
            {
                data: queryParams,
            },
            showDefaultMessage
        )
    },
    getAQLQuery(queryParams = {}){
        queryParams = modifyFilters(queryParams);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/aql/convertor",
            {
                data: queryParams,
            }
        )
    },
    getAQLV2Query(queryParams = {}, showDefaultMessage = true){
        queryParams = modifyFilters(queryParams);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/aql/v2/convertor",
            {
                data: queryParams,
            },
            showDefaultMessage,
        )
    },
    getBasicFiltersFromQuery(queryParams = {}, showDefaultMessage = true){
        queryParams = modifyFilters(queryParams);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/aql/convertor/basic",
            {
                data: queryParams,
            },
            showDefaultMessage
        )
    },
    getAllByAQL(queryParams = {}){
        // queryParams = modifyFilters(queryParams);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/aql/list",
            {
                data: queryParams,
            }
        )
    },
    getAllReviewed(queryParams = {}){
        queryParams = modifyFilters(queryParams);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/under-review",
            {
                data: queryParams,
            }
        )
    },
    getUnderReviewFilterValues(data){
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/under-review/filters",
            {
                data
            },
        )
    },
    exportUnderReviewCSV(queryParams = {}){
        return makeCancellableAPIRequest(
            "POST",
            "/api/findings/under-review/export",
            {
                data: queryParams,
            }
        )
    },
    getFilterValues(data = {}, BUaggregation = false, showDefaultMessage = true){
        data = modifyFilters(data);
        const path = BUaggregation ? "/api/businessunits/dashboard/findings/findings-filters": "/user/findings/findings-filters"
        return makeCancellableAPIRequest(
            "POST",
            path,
            {
                data
            },
            showDefaultMessage
        )
    },
    getFilterValuesV3(fieldName, searchValue, filters) {
        return makeCancellableAPIRequest(
            "POST",
            "api/v3/findings/filters",
            {
                data: {
                    field: fieldName,
                    value: searchValue,
                    filters: modifyFilters({ filters }).filters,
                }
            },
        )
    },
    getSingleFilterValues(fieldName, searchValue, filters){
        const path = "/user/findings/v2/findings-filters";
        const { promise, cancel } = makeCancellableAPIRequest("POST", path, {
            data: {
                field: fieldName,
                value: searchValue,
                filters: modifyFilters({ filters }).filters,
            },
        });
        return {
            cancel,
            promise: promise.then(
                (response) => response?.data?.[fieldName] ?? []
            ),
        };
    },
    getFilterValuesAql(data = {}, showDefaultMessage = true){
        data = modifyFilters(data);
        const path = "/user/findings/aql/findings-filters"
        return makeCancellableAPIRequest(
            "POST",
            path,
            {
                data
            },
            showDefaultMessage
        )
    },
    get(id, showDefaultMessage = true){
        return makeCancellableAPIRequest(
            "GET",
            `/user/findings/${id}`,
            {},
            showDefaultMessage
        );
    },
    update(id, data) {
        return failSafe(
            APIClient.put(`/user/findings/${id}`, data)
        )
    },
    getStats({ filters, startDate, endDate, selectedDuration, ...rest }, BUaggregation = false) {
        filters = getFindingAPIDurationFilter(filters, selectedDuration, startDate, endDate);
        const path = BUaggregation ? "/api/businessunits/dashboard/findingStats" : "/user/findings/findingStats"
        return makeCancellableAPIRequest(
            "POST",
            path,
            {
                data: {
                    ...rest,
                    filters
                }
            }
        )
    },
    getStatsByAppSecStatus({ filters, startDate = undefined, endDate = undefined, selectedDuration = undefined, ...rest }) {
        filters = getFindingAPIDurationFilter(filters, selectedDuration, startDate, endDate);
        filters = modifyFilters({ filters }).filters;
        const path = "/user/findings/findingStats/by-appsec-status"
        return makeCancellableAPIRequest(
            "POST",
            path,
            {
                data: {
                    ...rest,
                    filters
                }
            }
        )
    },
    getStatsV2({ filters, startDate, endDate, selectedDuration, ...rest }) {
        filters = getFindingAPIDurationFilter(filters, selectedDuration, startDate, endDate);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/findingStats/riskScore",
            {
                data: {
                    ...rest,
                    filters
                }
            }
        )
        // return failSafe(
        //     APIClient.post(`/user/findings/findingStats`,  queryParams)
        // );
    },
    getStatsAll(data = {},BUaggregation = false, showDefaultMessage) {
        data.filters = getFindingAPIDurationFilter(data.filters, data.selectedDuration, data.startDate, data.endDate);
        data = modifyFilters(data);
        data.timezone = moment.tz.guess();
        const path = BUaggregation ? "/api/businessunits/dashboard/findingStats/all" : "/user/findings/findingStats/all"
        return makeCancellableAPIRequest(
            "POST",
            path,
            {
                data
            },
            showDefaultMessage
        )
    },
    getStatsCount(data){
        const request = FindingAPI.getStatsAll(data);
        return {
            cancel: request.cancel,
            promise: request.promise.then(response => convertFindingStatsToCount(response))
        }
    },
    getFilterCounts({ aggField }) {
        const request = FindingAPI.getFilterValuesV3(aggField, "", {});
        return {
            cancel: request.cancel,
            promise: request.promise.then(response => {
                const pairs = response?.data?.filtersCountPairValues;
                if (!pairs) return [];
                const result = new Map();
                Object.entries(pairs).forEach(([id, val]) => {
                    if (val !== null && typeof val === 'object' && !Array.isArray(val) && typeof val.count === 'number') {
                        result.set(id, val.count);
                        if (typeof val.name === 'string') result.set(val.name, val.count);
                    } else if (typeof val === 'number') {
                        result.set(id, val);
                    }
                });
                return Array.from(result.entries()).map(([id, count]) => ({ id, count }));
            })
        }
    },
    getGroupStats(data = {}) {
        data = modifyFilters(data);
        const path = "/user/findings/groups/count";
        return makeCancellableAPIRequest(
            "POST",
            path,
            {
                data
            },
        )
    },

    getStatsMetric(data = {}) {
        data.filters = getFindingAPIDurationFilter(data.filters, data.selectedDuration, data.startDate, data.endDate);
        data = modifyFilters(data);
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/findingStats/metric",
            {
                data
            }
        )
    },

    getDateFieldAggregate(data = {}) {
        data = modifyFilters(data);
        return makeCancellableAPIRequest(
            "POST",
            "/api/finding/metric/single",
            {
                data
            }
        )
    },

    getStatsForLastStatusChange(data, showDefaultMessage){
        data.ignoreMitigated = null;
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/findingStats/last-change-status",
            {
                data,
            },
            showDefaultMessage
        );
    },
    getOwnedStatsAll(data = {}) {
        data.filters = getFindingAPIDurationFilter(data.filters, data.selectedDuration, data.startDate, data.endDate);
        data.ignoreMitigated = null;
        data.secAggField = "status";
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/findingStats/owned",
            {
                data
            }
        )
    },
    getEvents(id, params = {}) {
        const defaultParams = {
            page: 0,
            size: 10,
            timezone: moment.tz.guess()
        };

        return makeCancellableAPIRequest(
            "GET",
            `/user/findings/${id}/events`,
            {
                params: { ...defaultParams, ...params }
            }
        )
    },
    getComments(id, params = {}) {
        const defaultParams = {
            page: 0,
            size: 10
        };

        return makeCancellableAPIRequest(
            "GET",
            `/user/findings/${id}/comment`,
            {
                params: { ...defaultParams, ...params }
            }
        )
    },
    postComment(id, {htmlText = "", markdownText, userIds, attachments}) {
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/${id}/comment`,
            {
                data: { note: htmlText, markdownText, userIds, attachments }
            }
        )
    },
    editComment(id, {htmlText = "", markdownText, userIds, attachments, commentId}) {
        return makeCancellableAPIRequest(
            "PUT",
            `/user/findings/${id}/comment/${commentId}`,
            {
                data: { note: htmlText, markdownText, userIds, attachments }
            }
        )
    },
    getAcceptRiskCount(findingId){
        return makeCancellableAPIRequest(
            "GET",
            `/user/findings/${findingId}/acceptRiskCount`
        )
    },
    getAcceptRiskCountBulk(findingIds){
        return makeCancellableAPIRequest(
            "POST",
            '/user/findings/acceptRiskCount',
            {
                data: findingIds
            }
        )
    },
    createManualFinding(data){
        return makeCancellableAPIRequest(
            "POST",
            "/user/findings/manual",
            {
                data,
            }
        )
    },
    similarFindings: {
        count(data){
            return makeCancellableAPIRequest(
                "POST",
                `/user/similar/count`,
                {
                    data,
                },
                false
            )
        },
        getRule(data){
            return makeCancellableAPIRequest(
                "POST",
                `/user/similar/future/rule`,
                {
                    data,
                }
            )
        },
        /**
         * 
         * @param {*} data 
         * @param {String} type - accept-risk | severity | cateogry | false-positive | confirm | open
         * @returns 
         */
        update(data, type){ 
            return makeCancellableAPIRequest(
                "POST",
                `/user/similar/${type}`,
                {
                    data,
                }
            )
        },
        getSimilarFindings(data){
            return makeCancellableAPIRequest(
                "POST",
                `/user/similar/findings`,
                {
                    data,
                }
            )
        },
    },
    updateFinding(data, findingId){
        return makeCancellableAPIRequest(
            "PUT",
            `/user/findings/${findingId}`,
            {
                data,
            }
        )
    },
    downloadCsv(queryParams = {}){
        queryParams = modifyFilters(queryParams);
        queryParams.ticketStatusRequired = true;

        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/download/v2/csv`,
            {
                data: queryParams,
            }
        )
    },
    downloadCsvV2(queryParams = {}){
        queryParams = modifyFilters(queryParams);
        queryParams.ticketStatusRequired = true;

        return makeCancellableAPIRequest(
            "POST",
            `/api/findings/download/csv`,
            {
                data: queryParams,
            }
        )
    },
    downloadPdf(queryParams = {}){
        queryParams = modifyFilters(queryParams);
        queryParams.ticketStatusRequired = true;
        
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/download/v2/pdf`,
            {
                data:queryParams,
            }
        )
    },
    uploadAttachment(id,data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/${id}/comment/attachment`,
            {
                data
            }
        )
    },
    uploadManualAttachment(data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/upload/attachment`,
            {
                data
            }
        )
    },
    uploadFindingCSV(data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/upload/csv`,
            {
                data,
                headers: {
                    'content-type': 'multipart/form-data'
                }
            }
        )
    },
    uploadFindingCSV_V2(data){
        // get TimeZone
        const uploadTimezone = moment.tz.guess();
        return makeCancellableAPIRequest(
            "POST",
            `/api/v2/findings/csv/upload`,
            {
                data: {
                    ...data,
                    uploadTimezone
                },
            }
        )
    },
    uploadFindingAttachment(findingId,data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/${findingId}/attachment`,
            {
                data
            }
        )
    },
    uploadFindingAttachmentToTicket(findingId, data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/${findingId}/attachmentTicket`,
            {
                data
            }
        )
    },
    deleteAttachment(findingId,attachmentId){
        return makeCancellableAPIRequest(
            "DELETE",
            `/user/findings/${findingId}/attachment/${attachmentId}`
        )
    },
    checkforTicketConfig(buId,apId,aspId){
        return makeCancellableAPIRequest(
            "GET",
            `/api/tenant/businessUnits/${buId}/products/${apId}/subProducts/${aspId}/ticketConfig`
        )
    },
    moveFinding(data){
        return makeCancellableAPIRequest(
            "POST",
            `/api/tenant/move-findings`,
            {
                data
            }
        )
    },
    moveFindingV2(id, envId, data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/${id}/changeEnv/${envId}`,
            {
                data
            }
        )
    },
    downloadAttachment(id,attachmentId){
        return makeCancellableAPIRequest(
            "GET",
            `/user/findings/${id}/attachment/${attachmentId}`
        )
    },
    getAttachments(toolName, params){
        return makeCancellableAPIRequest(
            "GET",
            `/user/tools/generic/${toolName.toUpperCase()}/attachments`,
            {params},
            false
        )
    },
    getToolsComments(tool,params){
        if(typeof tool !== 'string' || tool.trim() === ''){
            return Promise.reject('tool must be a non-empty string, received tool:'+tool);
        }
        return makeCancellableAPIRequest(
            'GET',
            `/user/tools/generic/${tool}/comments`,
            {
                params
            },
            false
        )
    },
    remediation(id, data) {
        return makeCancellableAPIRequest(
            "POST",
            `/user/findings/remediate/${id}`,
            {
                data,
            }
        )
    },
    updateFindingField(findingId, fieldType, data){
        return makeCancellableAPIRequest(
            'PUT',
            `/user/findings/${fieldType}/${findingId}`,
            {
                data
            }
        )
    },
    updateFindingStatus:{
        suppressed(id, data, showErrorMessage = true){
            return makeCancellableAPIRequest(
                "PUT", 
                `/user/findings/suppressed/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
        triage(id, data, showErrorMessage = true){
            return makeCancellableAPIRequest(
                "PUT", 
                `/user/findings/triage/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
        acceptrisk(id, data,showErrorMessage = true) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/accept-risk/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
        in_progress(id, data, showErrorMessage = true){
            return makeCancellableAPIRequest(
                "PUT", 
                `/user/findings/inProgress/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
        falsepositive(id, data, showErrorMessage =true) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/false-positive/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
        confirmed(id, data, showErrorMessage = true) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/confirm/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
        open(id, data, showErrorMessage = true) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/reopen/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
        changeowner(id, data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/assign-owner/${id}`,
                {
                    data,
                }
            )
        },
        severity(id, data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/severity/${id}`,
                {
                    data,
                }
            )
        },
        category(id, data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/category/${id}`,
                {
                    data,
                }
            )
        },
        controlled(id, data, showErrorMessage = true) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/controlled/${id}`,
                {
                    data,
                },
                showErrorMessage
            )
        },
    },
    mitigateFinding:{

        single(findingId, data){
            return makeCancellableAPIRequest(
                'PUT',
                `/user/findings/mitigated/${findingId}`,
                {
                    data
                }
            )
        },

        bulkOrsimilar(data){
            return makeCancellableAPIRequest(
                'POST',
                `/user/similar/mitigated`,
                {
                    data
                }
            )
        }
    },
    futureRules: {
        getAll(params = {}) {
            return makeCancellableAPIRequest(
                'GET',
                '/api/findings/future-rule/filter-data',
                {
                    params
                }
            )
        },
        getFilters(){
            return makeCancellableAPIRequest(
                'GET',
                '/api/findings/future-rule/filters'
            )
        },
        updateRule(id, data){
            return makeCancellableAPIRequest(
                'PUT',
                `/api/findings/future-rule/${id}`,
                {
                    data
                }
            )
        },
        deleteRule(id){
            return makeCancellableAPIRequest(
                'DELETE',
                `/api/findings/future-rule/${id}`
            )
        }
    },
    getTrends({ filters, startDate, endDate, selectedDuration, ...data },  BUaggregation = false) {
        filters = getFindingAPIDurationFilter(filters, selectedDuration, startDate, endDate);
        if(filters.foundOn) {
            filters.createdAt = filters.foundOn
            delete filters.foundOn
        }
        const path = BUaggregation ? `/api/businessunits/dashboard/findingStats/trend` : `/user/findingStats/v2/trend`
        return makeCancellableAPIRequest(
            'POST',
            path,
            {
                data: {
                    filters,
                    fields: ["status", "severity"],
                    ...data
                }
            }
        )
    },
    getTrendsByPRoduct({ filters, startDate, endDate, selectedDuration }) {
        filters = getFindingAPIDurationFilter(filters, selectedDuration, startDate, endDate);
        if(filters.foundOn) {
            filters.createdAt = filters.foundOn
            delete filters.foundOn
        }
        return makeCancellableAPIRequest(
            'POST',
            '/user/findingStats/trend-by-product',
            {
                data: {
                    filters,
                }
            }
        )
    },
    getSLATrends({ filters, startDate, endDate, selectedDuration, ...data }, BUaggregation = false) {
        filters = getFindingAPIDurationFilter(filters, selectedDuration, startDate, endDate);
        if(filters.foundOn) {
            filters.createdAt = filters.foundOn
            delete filters.foundOn
        }
        const path  = BUaggregation ? `/api/businessunits/dashboard/findingSla/sla-stats` : `/user/findingSla/v2/sla-stats`
        return makeCancellableAPIRequest(
            'POST',
            path,
            {
                data: {
                    filters,
                    ...data
                    // fields: ["status", "severity"],
                }
            }
        )
    },
    getOWASPStats(data) {
        return makeCancellableAPIRequest(
            'POST',
            `/user/findingStats/OWASP`,
            {
                data
            }
        );
    },
    getCommitDetails(findingId, commitUrl) {
        return makeCancellableAPIRequest(
            'GET',
            `/user/findings/commit/${findingId}`,
            {
                params: { commitUrl }
            },
            false
        );
    },
    getEmergingThreats(data) {
        return makeCancellableAPIRequest(
            'POST',
            '/user/findings/emerging-threats',
            {
                data
            }
        );
    },
    checkIfFileIsAllowed(data) {
        return makeCancellableAPIRequest(
            'POST',
            '/user/findings/file',
            {
                data
            }
        );
    },
    bulk: {
        updateFindings(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/update",
                {
                    data: addExtraParams(data),
                }
            )
        },
        triage(data){
            return FindingAPI.bulk.updateFindings(data)
        },
        inProgress(data){
            return FindingAPI.bulk.updateFindings(data);
        },
        controlled(data){
            return FindingAPI.bulk.updateFindings(data);
        },
        acceptRisk(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/accept-risk",
                {
                    data: addExtraParams(data),
                }
            )
        },
        suppressed(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/suppressed",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeTriage(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-triage",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeInProgress(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-InProgress",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeAcceptRisk(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-accept-risk",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeSuppressed(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-suppressed",
                {
                    data: addExtraParams(data),
                }
            )
        },
        confirmed(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/confirm",
                {
                    data: addExtraParams(data),
                }
            )
        },
        severity(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/severity",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeSeverity(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-severity",
                {
                    data: addExtraParams(data),
                }
            )
        },
        category(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/category",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeCategory(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-category",
                {
                    data: addExtraParams(data),
                }
            )
        },
        assignOwner(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/assign-owner",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeAssignOwner(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-assignee",
                {
                    data: addExtraParams(data),
                }
            )
        },
        reopen(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/reopen",
                {
                    data: addExtraParams(data),
                }
            )
        },
        falsePositive(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/false-positive",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeFalsePositive(data){
            return makeCancellableAPIRequest(
                "PUT",
                "api/propose-false-positive",
                {
                    data: addExtraParams(data),
                }
            )
        },
        basicDetails(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/change-basic-details",
                {
                    data: addExtraParams(data),
                }
            )
        },
        basicDetailsV2(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/v2/bulk/change-basic-details",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeBulk(data) {
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeBasicDetails(data){
            return makeCancellableAPIRequest(
                "PUT",
                "api/propose-basic-change",
                {
                    data: addExtraParams(data),
                }
            )
        },
        dueDate(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/change-duedate",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeDueDate(data){
            return makeCancellableAPIRequest(
                "PUT",
                "/api/propose-due-date",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeAcceptReject(data, type) {
            return makeCancellableAPIRequest(
                "PUT",
                `/api/approve-reject/${type}`,
                {
                    data
                }
            )
        },
        proposeAcceptRejectCustomFields(data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/api/propose/approve-reject/custom-fields`,
                {
                    data
                }
            )
        },
        proposeBulkAction({ findingFilterRequest = {}, ...data }) {
            findingFilterRequest = modifyFilters(findingFilterRequest);
            return makeCancellableAPIRequest(
                "PUT",
                `/api/approve-reject/proposal`,
                {
                    data: {
                        ...data,
                        findingFilterRequest,
                    }
                }
            )
        },
        tagUpdate(data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/findingTags`,
                {
                    data
                }
            )
        },
        mitigated(data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/bulk/mitigated`,
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeMitigated(data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/api/propose-mitigated`,
                {
                    data: addExtraParams(data),
                }
            )
        },
        changeProjects(data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/bulk/assign-project`,
                {
                    data: addExtraParams(data),
                }
            )
        },
        addToException(data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/bulk/assign-risk-register`,
                {
                    data: addExtraParams(data),
                }
            )
        },
        addToAssessment(data) {
            return makeCancellableAPIRequest(
                "PUT",
                `/user/findings/bulk/assign-assessment`,
                {
                    data: addExtraParams(data),
                }
            )
        },
        detachTicket(data) {
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/detach-ticket",
                {
                    data: addExtraParams(data),
                }
            )
        },
        proposeControlled(data){
            return makeCancellableAPIRequest(
                "PUT",
                "api/propose-controlled",
                {
                    data: addExtraParams(data),
                }
            )
        },
        correlateFindings(data) {
            return makeCancellableAPIRequest(
                "PUT",
                "/user/findings/bulk/correlated",
                {
                    data: addExtraParams(data),
                }
            )
        }
    },
    getManualFindingTemplates() {
        return makeCancellableAPIRequest(
            'GET',
            '/api/tenant-config',
            { 
                params: {
                    configType: 'MANUAL_FINDING_TEMPLATE',
                }
            }
        )
    },
    addManualFindingTemplates({ name, template }) {
        return failSafe(
            APIClient.post('/api/tenant-config/MANUAL_FINDING_TEMPLATE', {
                configType: 'MANUAL_FINDING_TEMPLATE',
                data: {
                    name,
                    template,
                }
            })
        );
    },
    updateManualFindingTemplates({ name, oldName, template }) {
        return failSafe(
            APIClient.put('/api/tenant-config/MANUAL_FINDING_TEMPLATE', {
                configType: 'MANUAL_FINDING_TEMPLATE',
                data: {
                    name,
                    oldName,
                    template,
                }
            })
        );
    },
    deleteManualFindingTemplates(name) {
        return failSafe(
            APIClient.delete('/api/tenant-config/MANUAL_FINDING_TEMPLATE', {
                data: {
                    configType: 'MANUAL_FINDING_TEMPLATE',
                    data: {
                        name,
                    }
                }
            })
        );
    },
    getSeverityStats(queryParams={}, allBUAggregated) {
        queryParams = modifyDate(queryParams);
        return makeCancellableAPIRequest(
            "POST",
            allBUAggregated ? `/api/businessunits/dashboard/findingStats/severity-stats` : `/user/findings/findingStats/severity-stats`,
            { data: queryParams }
        )
    },
    getCWEs({ size, page, name = "" }) {
        const { promise, cancel } = makeCancellableAPIRequest(
            'GET',
            'compliance/vulnerability/search',
            { 
                params: {
                    type: "CWE",
                    size, page ,
                    search: name
                }
            }
        )
        const _promise = promise.then(response => {
            response.content = _.map(response.content, cwe => cwe.replace("CWE-", ""))
            return response;
        });
        return { cancel, promise: _promise };
    },
    getCVEs({ size, page, name = "" }) {
        return makeCancellableAPIRequest(
            'GET',
            'compliance/vulnerability/search',
            { 
                params: {
                    type: "CVE",
                    size, page ,
                    search: name
                }
            }
        )
    },
    runPatch(configurationId, findingId) {
        return failSafe(
            APIClient.post(`/api/tools/automox/${configurationId}/patchFinding`, {
                "findingId": findingId,
            })
        );
    },
    getFindingsStatsAndTrend({ endDate, selectedDuration = undefined, statsDate = undefined, ...params }) {
        if (!statsDate) {
            if (selectedDuration) {
                statsDate = getStartAndEndDateForQuickOption({ label: selectedDuration })[1];
            }
            statsDate = moment(endDate).toDate().toUTCString();
        }
        return makeCancellableAPIRequest(
            'POST',
            '/api/dashboard/findings/trend-for-a-day/stats',
            { 
                data: {
                    ...params,
                    statsDate,
                },
            }
        )
    },
    getTrainingAIDetails(integrationType, cweId, programmingLanguage){
        return makeCancellableAPIRequest(
            'POST',
            '/user/findings/training/data',
            { 
                data: {
                    integrationType : integrationType, 
                    mappingList: 'cwe',
                    mappingKey: cweId,
                    programmingLanguage : programmingLanguage
                }
            }
        )
    },
    getDueDateAverage(data){
        data = modifyDate(data);
        return makeCancellableAPIRequest(
            'POST',
            '/user/findings/due-date-average',
            { 
                data
            }
        )
    },
    triggerSyncBDStatus(findingID, data){
        return makeCancellableAPIRequest(
            'PUT',
            `/user/findings/sync-finding-status/${findingID}`,
            { 
                data
            }
        )
    },
    getCorrelatedFindings(findingId, data) {
        return makeCancellableAPIRequest(
            'POST',
            `/api/findings/correlated-finding/${findingId}`,
            {
                data
            }
        )
    },
    deleteCorrelatedV2(findingId, correlatedFindingId, correlationId) {
        return makeCancellableAPIRequest(
            "DELETE",
            `/api/findings/${findingId}/correlated/${correlatedFindingId}/${correlationId}`,
        )
    },
    correlateFindingV2(data){
        return makeCancellableAPIRequest(
            'POST',
            `/api/findings/correlated`,
            { 
                data
            }
        )
    },
    correlateFinding(data){
        return makeCancellableAPIRequest(
            'POST',
            `/user/findings/correlated`,
            { 
                data
            }
        )
    },
    deleteCorrelated(findingId, deleteId) {
        return makeCancellableAPIRequest(
            "DELETE",
            `/user/findings/${findingId}/correlated/${deleteId}`,
        )
    },
    getFindingDescription(findingId){
        return makeCancellableAPIRequest(
            'GET',
            `/user/findings/description/s3/${findingId}`
        )
    },
    updateException(findingId, data) {
        return makeCancellableAPIRequest(
            "PUT",
            `/user/findings/update-risk-register/${findingId}`,
            {
                data
            }
        )
    },
    getAcceptRiskReasons() {
        const request = GlobalSettingAPI.getAcceptRiskReason();
        request.promise = request.promise.then(r => r.reasons);
        return request;
    },
    getControlledReasons() {
        const request = GlobalSettingAPI.getControlledReason();
        request.promise = request.promise.then(r => r.reasons);
        return request;
    },
    updateDraftFindings(id, data) {
        return {
            cancel: noop,
            promise: new Promise((resolve) => {
                setTimeout(() => {
                    const draftFindings = JSON.parse(localStorage.getItem("draftFindings"));
                    const index = draftFindings.findIndex((f) => f.id === id);
                    if(index !== -1){
                        draftFindings[index] = data;
                    }
                    localStorage.setItem("draftFindings", JSON.stringify(draftFindings));
                    resolve();
                }, random(100, 500))
            })
        }
    },
    addCustomFields(data, findingId) {
        return makeCancellableAPIRequest(
            "PUT",
            `/user/findings/custom-fields/${findingId}`,
            {
                data
            }
        )
    },
    derivedAttribute(data) {
        return makeCancellableAPIRequest(
            "POST",
            '/api/findings/derived-attribute',
            {
                data
            }
        )
    },
    validateCustomField(data) {
        return makeCancellableAPIRequest(
            "POST",
            '/api/findings/custom-fields/validate',
            {
                data
            }
        )
    },
    getAQLSupportedFields() {
        return makeCancellableAPIRequest(
            "GET",
            'api/findings/aql/fields'
        )
    },
    getAQLMetadataV2(params = {}) {
        return makeCancellableAPIRequest(
          "GET",
          "/api/aql/metadata/FINDINGS",
          { params },
        );
    },
    getOverallScore(data) {
        return makeCancellableAPIRequest(
            "POST",
            '/api/cvss/calculate',
            {
                data
            }
        )
    },
    getFindingsByDueDate(params){
        return makeCancellableAPIRequest(
            "POST",
            `/api/dashboard/finding/trend-by-due-date/stats`,
            {
                data: params,
            }
        );
    },
    getFindingsAggByDateField(payload){
        const modifyPayload = modifyFilters(payload)
        return makeCancellableAPIRequest(
            "POST",
            `/user/findingStats/group/byDate`,
            {
                data: modifyPayload,
            }
        );
    },
    getTimelineStats(data){
        return makeCancellableAPIRequest(
            "POST",
            `/user/findingStats/timeline`,
            {
                data,
            }
        );
    },
    getGroupedTimelineStats(data){
        return makeCancellableAPIRequest(
            "POST",
            `/api/v2/finding-stats/timeline/group-by`,
            {
                data,
            }
        );
    },
    updateCRACompliance(findingId, data) {
    return makeCancellableAPIRequest("PUT", `/api/findings/${findingId}/compliance`, {
      data,
    }, false)}
}

export default FindingAPI;

const modifyDate = (params) => {
    let startDate = params.startDate;
    let endDate = params.endDate;
    if (!_.isEmpty(params.selectedDuration)) {
        const [start, end] = getStartAndEndDateForQuickOption({ label: params.selectedDuration, utcString: true });
        startDate = start;
        endDate = end;
    }
    if (startDate && endDate) {
        params.startDate = moment.utc(startDate).format();
        params.endDate = moment.utc(endDate).format();
    }
    return params;
}

const numberRangeFields = ["baseScore", "impactScore", "exploitabilityScore", "findingScore", "aatiScore", "assetScore", "epssScore", "correlatedFindingsCount", "versionFindingsCount", "riskScore"]

export const modifyFilters = (queryParams) => {

    // ENG-77040: Product and Subproduct will never be null for a finding
    if (_.has(queryParams.filters, 'product')) {
        queryParams.filters.product = _.filter(queryParams.filters.product, id => id !== null);
    }

    if (_.has(queryParams.filters, 'subProduct')) {
        queryParams.filters.subProduct = _.filter(queryParams.filters.subProduct, id => id !== null);
    }

    if (queryParams.sortColumn === "") {
        delete queryParams.sortColumn
    }
    if (queryParams.sortOrder === "") {
        delete queryParams.sortOrder
    }
    if(!_.isEmpty(queryParams.filters)){
        if (queryParams.filters.existsFilters) {
            queryParams.existsFilters = queryParams.filters.existsFilters;
            delete queryParams.filters.existsFilters;
        }
        if (!_.isEmpty(queryParams.filters.applyToVersionFindings)) {
            queryParams.applyToVersionFindings = queryParams.filters.applyToVersionFindings[0];
            delete queryParams.filters.applyToVersionFindings;
        }
        if(!_.isEmpty(queryParams.filters.foundOnDate)){
            // this is Finding age filter
            const startDate  = queryParams.filters.foundOnDate[0] || null;
            const endDate = queryParams.filters.foundOnDate[1] || null;
            queryParams.filters.foundOnDate = [startDate, endDate];
        } else if(queryParams.filters.selectedDuration){
            // This is found on filter
            const newFilter = getFindingAPIDurationFilter(queryParams.filters, queryParams.filters.selectedDuration)
            queryParams.filters.foundOn = newFilter.foundOn
        } else if (!_.isEmpty(queryParams.filters.dateRange)) {
            queryParams.filters.foundOn = queryParams.filters.dateRange;
        }
        if (queryParams.filters.proposeAction) {
          const { filters } = queryParams;
          const { proposeAction, ...restFilters } = filters;
          if (!_.isEmpty(proposeAction)) {
            let newProposeActions = [...proposeAction];
            if (_.includes(proposeAction, "change_status")) {
              newProposeActions = _.filter(
                [
                  FINDING_STATUS.FALSE_POSITIVE,
                  FINDING_STATUS.ACCEPT_RISK,
                  FINDING_STATUS.MITIGATED,
                  FINDING_STATUS.SUPPRESSED,
                  FINDING_STATUS.TRIAGE,
                  FINDING_STATUS.IN_PROGRESS,
                  FINDING_STATUS.CONTROLLED,
                  ...proposeAction,
                ],
                (status) => status !== "change_status",
              );
            }
            _.forEach(newProposeActions, (value) => {
              if (value?.includes("PROPOSE")) {
                const key = _.camelCase(value);
                if(value?.includes("PROPOSE_CUSTOM") ){
                    restFilters["proposeCustomFields"] = restFilters["proposeCustomFields"] || []; 
                    restFilters["proposeCustomFields"].push(value.replace("PROPOSE_CUSTOM_", ""))
                }
                else {
                    restFilters[key] = null;
                }
              } else {
                if (!restFilters.proposeAction) {
                  restFilters.proposeAction = [];
                }
                restFilters.proposeAction.push(value);
              }
            });
          }

          if (restFilters.proposedFrom) {
            restFilters["proposedFrom"] =
              getProdSubProdEnvSequenceForProposeFromToFilters(
                "proposedFrom",
                restFilters,
              );
          }
      
          if (restFilters.proposedTo) {
            restFilters["proposedTo"] =
              getProdSubProdEnvSequenceForProposeFromToFilters(
                "proposedTo",
                restFilters,
              );
          }
          queryParams.filters = { ...restFilters, underReview: ["true"] }
        }

        if (queryParams.filters.vmHostIpAddresses) {
            const { vmHostIpAddresses, ...restFilters } = queryParams.filters;

            // -- If "Exclude - " is present at vmHostIpAddresses[0] strip it out and pass remaining as vmHostIpAddresses filter
            const excluded = vmHostIpAddresses[0] ? vmHostIpAddresses[0].indexOf(excludePrefix) !== -1 : false;
            queryParams.filters = {
                ...restFilters,
                excludeVmHostIp: [excluded],
                vmHostIpAddresses: excluded ? [vmHostIpAddresses[0]?.split(excludePrefix).pop()] : vmHostIpAddresses
            };
        }

        
        DATE_FILTER_API_SUPPORT.forEach(key => {
            const values = queryParams.filters[key];
            if(!isEmpty(values)){
                queryParams.filters[key] = convertDateFilterValueToDate(key, values);

                // if(!isEmpty(_parsedValues)){
                //     queryParams.filters[key] = _parsedValues;
                // } else {
                //     const filterValues = map(values, (value, i) => {
                //         let epoch = value;
                //         if(value === "now"){
                //             epoch = Date.now();
                //         } else if(isString(value) && !moment(value).isValid()){
                //             epoch = convertRelativeToAbsolute(value, i === 0 ? "start" : "end");
                //         }
                //         return moment(epoch).utc().format();
                //     });
                //     queryParams.filters[key] = filterValues;
                // }
            }
        })

        if (!_.isEmpty(queryParams.filters.scan)) {
            let [scanId] = queryParams.filters.scan;
            if (isString(scanId)) {
                scanId = scanId.replace(/[S-]/gi, '')
            }
            queryParams.filters.scan = isNaN(+scanId) ? [scanId] : [+scanId];
        }

        numberRangeFields.forEach(fieldsName => {
            const fieldValue = queryParams.filters?.[fieldsName]
            if(!_.isEmpty(fieldValue)) {
                const GEQ = fieldValue[0] || null
                const LEQ = fieldValue[1] || null

                queryParams.filters[fieldsName] = [GEQ, LEQ]
            }
        })

        if (!_.isEmpty(queryParams.filters.environmentName)) {
            queryParams.filters.environmentName = _.compact(queryParams.filters.environmentName);
        }
    }

    queryParams.ignoreMitigated = null;
    queryParams.ignoreDuplicate = true;
    if (queryParams?.filters) {
        queryParams.ignoreMitigated = _.isBoolean(queryParams.filters?.ignoreMitigated) ? queryParams.filters.ignoreMitigated : null
        queryParams.filters = _.omit(queryParams.filters, ['ignoreDuplicate', 'ignoreMitigated', 'selectedDuration', 'dateRange']);
    }
    return queryParams;
}

const getProdSubProdEnvSequenceForProposeFromToFilters = (key, newFilters) => {
  const productFilters = newFilters[key].filter((f) =>
    f.startsWith("product."),
  );
  const subProductFilters = newFilters[key].filter((f) =>
    f.startsWith("subProduct."),
  );
  const environmentFilters = newFilters[key].filter((f) =>
    f.startsWith("environmentName."),
  );

  const sortedProposeFrom = [];
  const minLength = Math.min(productFilters.length, subProductFilters.length);

  // Pair product-subProduct and insert environment once per pair
  for (let i = 0; i < minLength; i++) {
    sortedProposeFrom.push(productFilters[i], subProductFilters[i]);
    sortedProposeFrom.push(
      ...environmentFilters.filter((env) => !sortedProposeFrom.includes(env)),
    );
  }

  // Add remaining product or subProduct filters
  sortedProposeFrom.push(...productFilters.slice(minLength));
  sortedProposeFrom.push(...subProductFilters.slice(minLength));

  environmentFilters.forEach((env) => {
    if (!sortedProposeFrom.includes(env)) {
      sortedProposeFrom.push(env);
    }
  });

  // Add remaining filters that are not product, subProduct, or environment
  const otherFilters = newFilters[key].filter(
    (f) =>
      !f.startsWith("product.") &&
      !f.startsWith("subProduct.") &&
      !f.startsWith("environmentName."),
  );

  return [...sortedProposeFrom, ...otherFilters];
};

function addExtraParams(data) {
    return ({
        ...data,
        findingFilterRequest : {
            ...data.findingFilterRequest,
            ignoreMitigated: null
        }
    });
}
